<template>
  <el-dialog :visible.sync="visible"
             :before-close="handleClose"
             custom-class="inspector-dialog"
             :modal-append-to-body="false"
             width="98%"
             top="1vh"
             :show-close="false">
    <!-- Inspector Dialog Header Web -->
    <div slot="title" class="inspector-dialog-header">
      <div class="inspector-dialog-header-top">
        <i class="inspector-dialog-close-btn el-icon-close"
           @click="handleClose" />
        <RealogramPlanogramSwitcher v-model="selectedScene"
                                    :is-planogram-active="isPlanogramActive"
                                    :is-scene-loading="sceneStateLoading" />
        <div class="filter-search-wrapper">
          <div class="filters-wrapper">
            <!-- Class select -->
            <el-dropdown class="inspector-filter-dropdown"
                         size="large"
                         @command="filterClasses">
              <span class="el-dropdown-link inspector-cursor"
                    @mouseenter="filterByClass ? hoverClass = true : hoverClass = false"
                    @mouseleave="hoverClass = false">
                {{ normalizeString(filterByClass) || $t('Filter by Class') }}
                <i v-if="!hoverClass" class="inspector-cursor el-icon-arrow-down el-icon--right" />
                <i v-else
                   class="inspector-cursor el-icon-circle-close el-icon--right"
                   @click="clearClassFilterSelect" />
              </span>
              <el-dropdown-menu slot="dropdown" class="report-inspector-dialog-dropdown">
                <el-dropdown-item v-for="filter in classDropdownList"
                                  :key="filter.value"
                                  :command="filter.value">
                  <i v-if="filterByClass === filter.value" class="mdi mdi-check check" />
                  <span class="select-label">{{ filter.label }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <!-- Category select -->
            <el-dropdown class="inspector-filter-dropdown"
                         size="large"
                         :hide-on-click="false">
              <span class="el-dropdown-link inspector-cursor"
                    @mouseenter="selectedCategories.length ? hoverCategory = true : hoverCategory = false"
                    @mouseleave="hoverCategory = false">
                {{ $t('Filter by Category') }}
                <span v-if="selectedCategories.length" class="filter-count">{{ selectedCategories.length }}</span>
                <i v-if="!hoverCategory" class="inspector-cursor el-icon-arrow-down el-icon--right" />
                <i v-else
                   class="inspector-cursor el-icon-circle-close el-icon--right"
                   @click="clearCategoryFilterSelect" />
              </span>
              <el-dropdown-menu slot="dropdown" class="report-inspector-dialog-dropdown">
                <el-dropdown-item v-for="(category, index) in filterByCategorySelect"
                                  :key="index"
                                  :command="category"
                                  :class="{ 'selected' : selectedCategories.find(selected => selected === category) }"
                                  @click.native="handleCategoryDropdownItemClick(category)">
                  <i v-if="selectedCategories.find(selected => selected === category)" class="mdi mdi-check check" />
                  <span class="select-label">{{ category }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <!-- Brand select -->
            <el-dropdown class="inspector-filter-dropdown"
                         size="large"
                         :hide-on-click="false">
              <span class="el-dropdown-link inspector-cursor"
                    @mouseenter="selectedBrands.length ? hoverBrand = true : hoverBrand = false"
                    @mouseleave="hoverBrand = false">
                {{ $t('Filter by Brand') }}
                <span v-if="selectedBrands.length" class="filter-count">{{ selectedBrands.length }}</span>
                <i v-if="!hoverBrand" class="inspector-cursor el-icon-arrow-down el-icon--right" />
                <i v-else
                   class="inspector-cursor el-icon-circle-close el-icon--right"
                   @click="clearBrandFilterSelect" />
              </span>
              <el-dropdown-menu slot="dropdown" class="report-inspector-dialog-dropdown">
                <el-dropdown-item v-for="(brand, index) in filterByBrandSelect"
                                  :key="index"
                                  :command="brand"
                                  :class="{ 'selected' : selectedBrands.find(selected => selected === brand) }"
                                  @click.native="handleBrandDropdownItemClick(brand)">
                  <i v-if="selectedBrands.find(selected => selected === brand)" class="mdi mdi-check check" />
                  <span class="select-label">{{ brand }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div class="search-and-buttons">
            <el-input v-model="searchInput"
                      class="search-btn"
                      clearable
                      :placeholder="$t('Search')"
                      suffix-icon="mdi mdi-magnify"
                      @keyup.native="searchSkus" />
            <div class="scene-action-buttons-wrapper">
              <SceneFullScreenButton ref="webFullScreenButton" v-model="fullscreen" />
              <SceneExportButton />
            </div>
          </div>
        </div>
      </div>
      <div class="inspector-dialog-header-bottom">
        <el-switch v-model="recognitionResults"
                   :active-text="$t('View Recognition Results')"
                   :width="52"
                   :disabled="sceneStateLoading" />
        <div class="bottom-right-side-wrapper">
          <LegendsBar v-if="!recognitionResults" @legendClicked="handleClickLegend" />
          <ClassBar v-if="recognitionResults" @classClicked="handleClickClass" />
          <FillCropsBucketButton v-model="isFilledColors" />
        </div>
      </div>
    </div>
    <!-- Inspector Dialog Header Mobile -->
    <div slot="title"
         class="inspector-dialog-header-mobile">
      <div class="inspector-dialog-header-top-mobile">
        <SceneExportButton mobile-button />
        <RealogramPlanogramSwitcher v-model="selectedScene"
                                    :is-planogram-active="isPlanogramActive"
                                    :is-scene-loading="sceneStateLoading" />
        <i class="close-btn el-icon-close"
           @click="handleClose" />
      </div>
      <div class="inspector-dialog-header-top-mobile switch-search-group">
        <el-switch v-model="recognitionResults"
                   :active-text="$t('View Recognition Results')"
                   :width="52"
                   :disabled="sceneStateLoading" />
        <div class="btn-wrapper">
          <el-input v-model="searchInput"
                    class="search-btn"
                    :placeholder="$t('Search')"
                    suffix-icon="mdi mdi-magnify"
                    @keyup.native="searchSkus" />
          <el-button class="filter-btn"
                     round
                     icon="mdi mdi-filter-variant"
                     @click="innerDialogVisible = true" />
        </div>
      </div>
      <div class="inspector-dialog-header-bottom-mobile">
        <LegendsBar v-if="!recognitionResults"
                    :is-mobile="true"
                    @legendClicked="handleClickLegend" />
        <ClassBar v-if="recognitionResults" @classClicked="handleClickClass" />
        <FillCropsBucketButton v-model="isFilledColors" />
      </div>
      <!-- Mobile Filter Dialog -->
      <el-dialog width="100%"
                 class="inspector-inner-dialog"
                 :visible.sync="innerDialogVisible"
                 :show-close="false"
                 :modal="false"
                 append-to-body>
        <i slot="title"
           class="mdi mdi-filter-variant"
           @click="innerDialogVisible = false" />
        <!-- Class select -->
        <el-select v-model="filterByClass"
                   clearable
                   class="inspector-filter-select-mobile"
                   :placeholder="$t('Filter by Class')"
                   :popper-append-to-body="false"
                   popper-class="select-popper">
          <el-option v-for="filter in classDropdownList"
                     :key="filter.value"
                     :label="filter.label"
                     :value="filter.value" />
        </el-select>
        <!-- Category select -->
        <el-select v-model="selectedCategories"
                   multiple
                   collapse-tags
                   class="inspector-filter-select-mobile"
                   :placeholder="$t('Filter by Category')"
                   :popper-append-to-body="false"
                   popper-class="select-popper">
          <el-option v-for="(category, index) in filterByCategorySelect"
                     :key="index"
                     :label="category"
                     :value="category" />
        </el-select>
        <!-- Brand select -->
        <el-select v-model="selectedBrands"
                   multiple
                   collapse-tags
                   class="inspector-filter-select-mobile"
                   :placeholder="$t('Filter by Brand')"
                   :popper-append-to-body="false"
                   popper-class="select-popper">
          <el-option v-for="(brand, index) in filterByBrandSelect"
                     :key="index"
                     :label="brand"
                     :value="brand" />
        </el-select>
      </el-dialog>
    </div>

    <!-- Inspector Dialog Main Content -->
    <div class="inspector-dialog-main">
      <HorizontalLoading v-show="isLoading || imageSets.length <= 0 || sceneStateLoading"
                         slot="append" />
      <el-row class="inspector-scene-row">
        <div class="scene-fullscreen" :class="{ 'fullscreen-inspector': fullscreen }">
          <el-col class="result-scene"
                  :md="isGetActiveTabsHaveList ? 18 : 24"
                  :xs="24">
            <el-button v-if="isImagesetsHaveTaskTypes"
                       class="left-button"
                       :disabled="disabledPrevButton"
                       type="text"
                       @click="handlePreviousImage">
              <img :disabled="disabledPrevButton" src="/statics/left-arrow-slider.svg">
            </el-button>

            <div class="scene-header">
              <div class="scene-titles">
                <span v-if="selectedScene === this.$t('Realogram')" class="scene-title realogram-text">
                  {{ normalizeString($t(reportData.activity_category_name)) }} {{ normalizeString($t(reportData.display_type)) }}
                </span>
                <template v-if="isPlanogramActive && selectedScene === this.$t('Planogram')">
                  <span
                    class="scene-title planogram-text">
                    <span>
                      {{ normalizeString($t(reportData.activity_category_name)) }} {{ normalizeString($t(reportData.display_type)) }}
                    </span>
                    <router-link target="_blank"
                                 :to="referenceImageSetRoute"
                                 class="dashboard"
                                 @click.native="openDialog = false">
                      <small v-if="!isMobile">{{ referenceImageSetReport.reference_title }}&nbsp;</small>
                      <small v-else>
                        {{ $t("View Planogram") }}&nbsp;
                      </small>
                      <i class="mdi mdi-open-in-new" />
                    </router-link>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span class="show-on-mobile" v-html="planogramReversedText" />
                  </span>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span class="scene-title show-on-desktop" v-html="planogramReversedText" />
                </template>
              </div>
              <div class="scene-actions-wrapper">
                <BackgroundAlphaSlider v-model="bgAlpha" />
                <SceneZoomButton v-model="sceneZoomScaleValue"
                                 :selected-scene="selectedScene" />
              </div>
              <i v-if="fullscreen && !isMobile"
                 class="fullscreen-close-btn pointer-cursor mdi"
                 :class="fullscreen ? 'mdi-fullscreen-exit' : 'mdi mdi-fullscreen'"
                 @click="fullscreen = false" />
            </div>
            <keep-alive>
              <Scene v-if="selectedScene === this.$t('Realogram')"
                     ref="reportInspectorScene"
                     :mosaic="mosaicImage"
                     :image-sets="imageSets"
                     :report-data-images="reportDataImages"
                     :image-results="imageResults"
                     :report="report"
                     :bg-alpha="bgAlpha"
                     :skus="filteredSkuList"
                     :scene-zoom-scale-value="sceneZoomScaleValue.realogram"
                     :is-full-screen="fullscreen"
                     :is-filled-colors="isFilledColors"
                     :recognition-results="recognitionResults"
                     :image-set-id="reportData.id"
                     :non-sku-list="nonSkuList"
                     :is-sku-list-active="!recognitionResults && isGetActiveTabsHaveList"
                     :selected-image-number="selectedImageNumber"
                     :scene-title="`${reportData.activity_category_name}_${reportData.display_type}`"
                     @SceneStateLoading="handleSceneStateLoading" />
            </keep-alive>

            <keep-alive>
              <Scene v-if="isPlanogramActive && selectedScene === this.$t('Planogram')"
                     ref="referenceImageSetScene"
                     :image-sets="referenceImageSet"
                     :report-data-images="referenceImageSetReport.images"
                     :image-results="referenceImageResults"
                     :report="referenceImageSetReport.report"
                     :skus="filteredSkuList"
                     :bg-alpha="bgAlpha"
                     :reference="true"
                     :scene-zoom-scale-value="sceneZoomScaleValue.planogram"
                     :is-full-screen="fullscreen"
                     :is-filled-colors="isFilledColors"
                     :recognition-results="recognitionResults"
                     :reference-image-set-id="reportData.reference_image_set_id"
                     :non-sku-list="nonSkuList"
                     :is-sku-list-active="!recognitionResults && isGetActiveTabsHaveList"
                     :selected-image-number="selectedImageNumber"
                     :scene-title="`${reportData.activity_category_name}_${reportData.display_type}_Planogram`"
                     :is-reversed="reportData.reference_image_set_reversed"
                     @SceneStateLoading="handleSceneStateLoading" />
            </keep-alive>

            <el-button v-if="isImagesetsHaveTaskTypes"
                       class="right-button"
                       :disabled="disabledNextButton"
                       type="text"
                       @click="handleNextImage">
              <img src="/statics/right-arrow-slider.svg">
            </el-button>
          </el-col>
          <!-- Mobile Sku List -->
          <div v-if="mobileSkuCarouselList.length > 0"
               class="mobile-sku-list"
               :class="[{'sku-drawer-removed': !isGetActiveTabsHaveList}]">
            <SkuListItem v-for="sku in mobileSkuCarouselList"
                         :id="sku.id"
                         :key="sku.id"
                         :sku="sku"
                         :legend-task-state="sku.legendState ? sku.legendState : ''"
                         :scrollable-sku-list="true"
                         :remove-side-color="true"
                         border-color="#777" />
          </div>
        </div>

        <el-col v-show="isGetActiveTabsHaveList"
                class="side-sku-list"
                :md="6"
                hidden-md-and-down>
          <InspectorSkuList ref="inspectorSkuListWeb"
                            :report-data="reportData"
                            :filtered-sku-list="filteredSkuList"
                            :realogram-item-list="realogramItemList"
                            :missing-list="missingList"
                            :misplaced-list="misplacedList"
                            :excess-list="excessList"
                            :planogram-item-list="planogramItemList"
                            :out-of-stock-list="outOfStockList"
                            :in-stock-list="inStockList"
                            :is-planogram-active="isPlanogramActive" />
        </el-col>
      </el-row>

      <!-- Inspector Scene Mobile Bottom Section -->
      <div class="report-inspector-mobile-bottom-section">
        <!-- Show Realogram/Planogram Dialog Mobile Bottom section -->
        <div v-if="isGetActiveTabsHaveList"
             class="inspector-dialog-bottom-mobile">
          <div class="drawer-button-wrapper">
            <el-button type="info" @click="taskListDrawer = true">
              {{ $t('SKU LIST') }}
            </el-button>
          </div>
          <el-drawer :visible.sync="taskListDrawer"
                     direction="btt"
                     size="74.5%"
                     :append-to-body="true"
                     :modal="false"
                     :modal-append-to-body="true"
                     custom-class="custom-drawer"
                     :show-close="false"
                     :with-header="false">
            <InspectorSkuList :report-data="reportData"
                              :mobile-style="true"
                              :filtered-sku-list="filteredSkuList"
                              :realogram-item-list="realogramItemList"
                              :missing-list="missingList"
                              :misplaced-list="misplacedList"
                              :excess-list="excessList"
                              :planogram-item-list="planogramItemList"
                              :out-of-stock-list="outOfStockList"
                              :is-planogram-active="isPlanogramActive"
                              :in-stock-list="inStockList"
                              @closeSkuListDrawer="taskListDrawer = false" />
          </el-drawer>
        </div>
        <div class="mobile-scene-actions">
          <BackgroundAlphaSlider v-model="bgAlpha" />
          <SceneZoomButton v-model="sceneZoomScaleValue"
                           :selected-scene="selectedScene" />
          <SceneFullScreenButton ref="mobileFullScreenButton" v-model="fullscreen" />
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  reduce,
  uniq,
  chain,
  differenceWith,
  isEqual,
  isEmpty, includes
} from 'lodash';
import { mapState } from 'vuex';
import BackgroundAlphaSlider from '@/modules/report-inspector/components/BackgroundAlphaSlider';
import SceneZoomButton from '@/modules/report-inspector/components/SceneZoomButton';
import SceneFullScreenButton from '@/modules/report-inspector/components/SceneFullScreenButton';
import FillCropsBucketButton from '@/modules/report-inspector/components/FillCropsBucketButton';
import RealogramPlanogramSwitcher from '@/modules/report-inspector/components/RealogramPlanogramSwitcher';
import SceneExportButton from '@/modules/report-inspector/components/SceneExportButton';
import MobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import bus, { EVENTS } from '../report-inspector-scene/bus';
import InspectorBus from '../InspectorBus';

// Components
import InspectorSkuList from './InspectorSkuList';
import SkuListItem from './SkuListItem';
import Scene from './Scene';
import LegendsBar from './LegendsBar';
import ClassBar from './ClassBar';

export default {
  name: 'ReportInspectorDialog',
  components: {
    SceneExportButton,
    RealogramPlanogramSwitcher,
    FillCropsBucketButton,
    SceneFullScreenButton,
    SceneZoomButton,
    BackgroundAlphaSlider,
    Scene,
    InspectorSkuList,
    SkuListItem,
    LegendsBar,
    ClassBar
  },
  mixins: [MobileScreenControl],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    beforeCloseDialog: {
      type: Function,
      default() {}
    },
    mosaicImage: {
      type: String,
      default: ''
    },
    reportData: {
      type: Object,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    imageSets: {
      type: Array,
      required: true
    },
    reportDataImages: {
      type: Array,
      required: true
    },
    imageResults: {
      type: Array,
      default() {
        return [];
      }
    },
    report: {
      type: Object,
      default() {
        return {};
      }
    },
    skus: {
      type: Array,
      default() {
        return [];
      }
    },
    referenceImageSet: {
      type: Array,
      default() {
        return [];
      }
    },
    referenceImageSetReport: {
      type: Object,
      default() {
        return {};
      }
    },
    referenceImageResults: {
      type: Array,
      default() {
        return [];
      }
    },
    competitorSkuList: {
      type: Array,
      default() {
        return [];
      }
    },
    nonSkuList: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      isLoading: false,
      search: '',
      searchInput: '',
      searchStringList: [],
      recognitionResults: false,
      filterByClass: null,
      classDropdownList: [
        {
          label: 'All',
          value: 'all'
        },
        {
          label: 'SKU',
          value: 'sku'
        },
        {
          label: 'Competitor SKU',
          value: 'competitor_sku'
        }
      ],
      innerDialogVisible: false,
      planogramDrawer: false,
      taskListDrawer: false,
      sceneStateLoading: false,
      isFilledColors: false,
      sceneZoomScaleValue: {
        realogram: 0.4,
        planogram: 0.4
      },
      bgAlpha: 0.75,
      // for filter clearable button
      hoverClass: false,
      hoverCategory: false,
      hoverBrand: false,
      selectedScene: this.$t('Realogram'),
      selectedBrands: [],
      selectedCategories: [],
      selectedImageNumber: 1,
      fullscreen: false
    };
  },
  computed: {
    ...mapState(['Project', 'Imageset']),
    reportDataSkuList() {
      if (this.reportData.report && this.reportData.report.sku_list) {
        return this.reportData.report.sku_list;
      }

      return [];
    },
    competitorList() {
      return this.competitorSkuList.filter(sku => this.reportDataSkuList.some(reportSku => sku.id === reportSku.sku.id));
    },
    flatSkuList() {
      if (this.filterByClass === 'sku') {
        const dif = differenceWith(this.skus, this.competitorList, isEqual);

        return this.getFlatSkuList(dif);
      }

      if (this.filterByClass === 'competitor_sku') {
        return this.getFlatSkuList(this.competitorList);
      }

      return this.getFlatSkuList(this.skus);
    },
    filteredSkuList() {
      const { normalizeString } = this;

      let list = this.flatSkuList;

      if (this.selectedBrands.length) {
        list = list.filter(sku => this.selectedBrands.some(item => sku.brand === item));
      }

      if (this.selectedCategories.length) {
        list = list.filter(sku => this.selectedCategories.some(item => sku.category === item));
      }

      if (this.search !== '') {
        // eslint-disable-next-line
        let searchList = [];
        this.searchStringList.forEach((query) => {
          const singleSearch = query.toLowerCase().split(' ');

          list.forEach((item) => {
            const skuNameWords = normalizeString(item.display_name).toLowerCase().split(' ');

            if (this.checkIfWordExist(singleSearch, skuNameWords)) {
              const indexItem = searchList.findIndex(x => x.id === item.id);

              if (indexItem > -1) {
                this.$delete(searchList, indexItem);
              }
              // eslint-disable-next-line
              searchList.push(item);
            }
          });
        });

        return chain(searchList).orderBy(['display_name'], ['asc']).value();
      }

      return chain(list).orderBy(['display_name'], ['asc']).value();
    },
    distinctList() {
      return this.flatSkuList.reduce((acc, item) => {
        Object.keys(item).forEach((key) => {
          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push(item[key]);
          acc[key] = uniq(acc[key]);
        });

        return acc;
      }, {});
    },
    filterByCategorySelect() {
      const categorySelectList = this.distinctList.Category;

      if (categorySelectList) {
        categorySelectList.sort((a, b) => a.localeCompare(b));
      }

      return categorySelectList;
    },
    filterByBrandSelect() {
      const brandSelectList = this.distinctList.Brand;

      if (brandSelectList) {
        brandSelectList.sort((a, b) => a.localeCompare(b));
      }

      return brandSelectList;
    },
    // Realogram & Planogram & must_have list
    realogramItemList() {
      const onlyShelvesList = chain(this.reportData)
        .get('report.realogram')
        .result('modules', [])
        .map(m => m.shelves)
        .flatten()
        .map(s => s.item_groups)
        .flatten()
        .map(i => i.splits)
        .flatten()
        .map(s => s.items)
        .flatten()
        .value();

      const coolersList = chain(this.reportData)
        .get('report.realogram')
        .result('coolers', [])
        .map(m => m.modules)
        .flatten()
        .map(m => m.shelves)
        .flatten()
        .map(s => s.item_groups)
        .flatten()
        .map(i => i.splits)
        .flatten()
        .map(s => s.items)
        .flatten()
        .value();

      return onlyShelvesList || coolersList;
    },
    planogramItemList() {
      const onlyShelvesList = chain(this.reportData)
        .get('report.planogram')
        .result('modules', [])
        .map(m => m.shelves)
        .flatten()
        .map(s => s.item_groups)
        .flatten()
        .map(i => i.splits)
        .flatten()
        .map(s => s.items)
        .flatten()
        .value();

      const coolersList = chain(this.reportData)
        .get('report.planogram')
        .result('coolers', [])
        .map(m => m.modules)
        .flatten()
        .map(m => m.shelves)
        .flatten()
        .map(s => s.item_groups)
        .flatten()
        .map(i => i.splits)
        .flatten()
        .map(s => s.items)
        .flatten()
        .value();

      return onlyShelvesList || coolersList;
    },
    missingList() {
      const missingItems = this.reportDataSkuList.filter(item => (item.extra_infos.available && (item.extra_infos.realogram_count < item.extra_infos.planogram_count)));

      const planogramList = this.getReducedSkuList(this.filterByLegend('Missing', 'Planogram'), missingItems);
      const realogramList = this.getReducedSkuList(this.filterByLegend('Missing', 'Realogram'), missingItems);

      const missingList = this.getLegendRealogramPlanogramLists(realogramList, planogramList, 'missing');
      bus.missingList = missingList;

      return missingList;
    },
    outOfStockList() {
      const oosItems = this.reportDataSkuList.filter(item => !item.extra_infos.available);

      const planogramList = this.getReducedSkuList(this.planogramItemList, oosItems);
      const realogramList = this.getReducedSkuList(this.realogramItemList, oosItems);

      const oosList = this.getLegendRealogramPlanogramLists(realogramList, planogramList, 'out_of_stock');
      bus.outOfStockList = oosList;

      return oosList;
    },
    inStockList() {
      const inStockItems = this.reportDataSkuList.filter(item => item.extra_infos.available);

      const planogramList = this.getReducedSkuList(this.planogramItemList, inStockItems);
      const realogramList = this.getReducedSkuList(this.realogramItemList, inStockItems);

      const inStockList = this.getLegendRealogramPlanogramLists(realogramList, planogramList, 'in_stock');
      bus.inStockList = inStockList;

      return inStockList;
    },
    misplacedList() {
      const misplacedItems = this.reportDataSkuList.filter(item => (item.extra_infos.available && (item.extra_infos.misplaced_count > 0)));

      const planogramList = this.getReducedSkuList(this.filterByLegend('Misplaced', 'Planogram'), misplacedItems);
      const realogramList = this.getReducedSkuList(this.filterByLegend('Misplaced', 'Realogram'), misplacedItems);

      const misplacedList = this.getLegendRealogramPlanogramLists(realogramList, planogramList, 'misplaced');

      bus.misplacedList = misplacedList;

      return misplacedList;
    },
    excessList() {
      const excessItems = this.reportDataSkuList.filter(item => (item.extra_infos.available && (item.extra_infos.realogram_count > item.extra_infos.planogram_count)));

      const planogramList = this.getReducedSkuList(this.filterByLegend('Excess', 'Planogram'), excessItems);
      const realogramList = this.getReducedSkuList(this.filterByLegend('Excess', 'Realogram'), excessItems);

      const excessList = this.getLegendRealogramPlanogramLists(realogramList, planogramList, 'excess');

      bus.excessList = excessList;

      return excessList;
    },
    isGetActiveTabsHaveList() {
      return !isEmpty(this.Project.active.data.settings.sku_list_tabs);
    },
    activeSkuListTabs() {
      return this.Project.active.data.settings.sku_list_tabs;
    },
    isPlanogramActive() {
      return !!(this.reportData.reference_image_set_id && this.reportData.reference_image_sets_visit_id);
    },
    currentImageSetTaskType() {
      const currentImageSet = this.imageSets.find(imageSet => imageSet.image_set_id === this.Imageset.active.data.id);

      return currentImageSet ? currentImageSet.type : '';
    },
    isImagesetsHaveTaskTypes() {
      return includes(bus.imageSetTypes, this.currentImageSetTaskType);
    },
    disabledNextButton() {
      return this.reportDataImages.length === this.selectedImageNumber;
    },
    disabledPrevButton() {
      return this.selectedImageNumber === 1;
    },
    allLegendRealogramSkuList() {
      return [
        ...this.excessList.realogram,
        ...this.misplacedList.realogram,
        ...this.missingList.realogram,
        ...this.outOfStockList.realogram
      ];
    },
    allLegendPlanogramSkuList() {
      return [
        ...this.excessList.planogram,
        ...this.misplacedList.planogram,
        ...this.missingList.planogram,
        ...this.outOfStockList.planogram
      ];
    },
    mobileSkuCarouselList() {
      const { filteredSkuList } = this;
      const { selectedScene } = this;
      let skuList = [];

      if (selectedScene === this.$t('Realogram')) {
        skuList = filteredSkuList.map((item) => {
          const findItem = this.allLegendRealogramSkuList.find(sku => sku.sku.id === item.id);
          const findInPlanogramOOSList = bus.outOfStockList.planogram.find(sku => sku.sku.id === item.id);
          const findInPlanogramMissingList = bus.missingList.planogram.find(sku => sku.sku.id === item.id);

          if (findItem) {
            return { ...item, ...findItem };
          }

          if (findInPlanogramOOSList) {
            return { ...item, ...findInPlanogramOOSList };
          }

          if (findInPlanogramMissingList) {
            return { ...item, ...findInPlanogramMissingList };
          }

          return item;
        });
      } else if (selectedScene === this.$t('Planogram')) {
        skuList = filteredSkuList.map((item) => {
          const findItem = this.allLegendPlanogramSkuList.find(sku => sku.sku.id === item.id);

          if (findItem) {
            return { ...item, ...findItem };
          }

          return item;
        });
      }

      return skuList;
    },
    planogramReversedText() {
      return this.$t('Planogram Direction:  <b>{direction}</b>', { direction: this.reportData.reference_image_set_reversed ? this.$t('REVERSE') : this.$t('STANDART') });
    },
    referenceImageSetRoute() {
      const { Breadcrumbs, reportData } = this;

      if (this.$route.name === Breadcrumbs.embededReport) {
        return {
          ...this.$route,
          name: Breadcrumbs.embededReport,
          params: {
            ...this.$route.params,
            reportID: reportData.reference_image_set_id
          }
        };
      }

      return {
        name: Breadcrumbs.imagesetDetail,
        params: {
          visitId: reportData.reference_image_sets_visit_id,
          imagesetId: reportData.reference_image_set_id
        }
      };
    }
  },
  watch: {
    searchInput() {
      if (this.searchInput === '') {
        this.searchSkus();
      }
    },
    search() {
      if (this.search !== '') {
        bus.isFilterSelected = true;
        this.getSearchStringList();
      } else {
        bus.isFilterSelected = false;
      }
    },
    visible() {
      this.disableSelect(this.visible);
      if (!this.visible) {
        if (this.$refs.reportInspectorScene) {
          this.$refs.reportInspectorScene.destroyToolTip();
        }

        if (this.$refs.referenceImageSetScene) {
          this.$refs.referenceImageSetScene.destroyToolTip();
        }
      }
    },
    isFilledColors(isFilled) {
      bus.isFilledCrop = isFilled;

      bus.$emit(EVENTS.RE_RENDER_CROP);
    },
    fullscreen(value) {
      if (this.isMobile) {
        this.$refs.mobileFullScreenButton.isFullScreen = value;
      } else {
        this.$refs.webFullScreenButton.isFullScreen = value;
      }
    },
    recognitionResults(switchStatus) {
      bus.viewRecognitionResult = switchStatus;
    },
    filterByClass() {
      if (this.filterByClass && this.filterByClass !== 'all') {
        bus.isFilterSelected = true;
      } else if (isEmpty(this.selectedBrands) && isEmpty(this.selectedCategories)) {
        bus.isFilterSelected = false;
      }
    },
    selectedBrands: {
      deep: true,
      handler() {
        if (!isEmpty(this.selectedBrands)) {
          bus.isFilterSelected = true;
        } else if (!this.filterByClass && isEmpty(this.selectedCategories)) {
          bus.isFilterSelected = false;
        }
      }
    },
    selectedCategories: {
      deep: true,
      handler() {
        if (!isEmpty(this.selectedCategories)) {
          bus.isFilterSelected = true;
        } else if (!this.filterByClass && isEmpty(this.selectedBrands)) {
          bus.isFilterSelected = false;
        }
      }
    },
    selectedScene(selected) {
      bus.selectedScene = selected;
    }
  },
  created() {
    document.addEventListener('keydown', this.listenKeyboardEvents);
  },
  updated() {
    bus.$on('CROP_TAPPED', (crop) => {
      document.getElementById(crop.class.id).scrollIntoView({
        behavior: 'smooth'
      });
    });
  },
  destroyed() {
    bus.dragPositionForRealogram = { x: 0, y: 0 };
    bus.dragPositionForPlanogram = { x: 0, y: 0 };
    bus.sceneZoomScaleValue.realogram = 0.4;
    bus.sceneZoomScaleValue.planogram = 0.4;
  },
  methods: {
    listenKeyboardEvents(event) {
      switch (event.code) {
        case 'ArrowLeft':
          this.handlePreviousImage();
          break;

        case 'ArrowRight':
          this.handleNextImage();
          break;

        default:
          break;
      }
    },
    handleClose() {
      InspectorBus.isFullScreen = false;
      this.recognitionResults = false;

      this.beforeCloseDialog();
    },
    checkIfWordExist(singleSearch, skuNameWords) {
      return singleSearch.every((element) => {
        let answer = false;
        skuNameWords.forEach((item) => {
          if (item.includes(element)) {
            answer = true;
          }
        });

        return answer;
      });
    },
    getSearchStringList() {
      const { normalizeString } = this;
      let words = this.search.toLowerCase().split(',');

      words = chain(words).map(word => word.trim()).map(item => normalizeString(item)).value();

      const emptyIndex = words.indexOf('');
      if (emptyIndex > -1) {
        this.$delete(words, emptyIndex);
      }
      this.searchStringList = words;
    },
    searchSkus() {
      this.isLoading = true;
      clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.search = this.searchInput;
        this.isLoading = false;
      }, 1000);
    },
    getFlatSkuList(list) {
      return list.map((sku) => {
        const skuTags = reduce(sku.tags, (acc, tag) => {
          acc[tag.type] = tag.name;

          return acc;
        }, {});

        return {
          ...sku,
          ...skuTags
        };
      });
    },
    handleSceneStateLoading(value) {
      this.sceneStateLoading = value;
    },
    async handleClickClass(payload) {
      const classIndex = bus.selectedClasses.findIndex(selected => selected.id === payload.selectedClass.id);

      if (payload.isChecked) {
        if (classIndex === -1) {
          bus.selectedClasses.push(payload.selectedClass);
        }
      } else {
        bus.selectedClasses.splice(classIndex, 1);
      }

      bus.$emit(EVENTS.RE_RENDER_CROP);
    },
    async handleClickLegend(payload) {
      const legendIndex = bus.selectedLegends.findIndex(legend => legend.id === payload.legend.id);

      switch (payload.isChecked) {
        case true:
          if (legendIndex === -1) {
            bus.selectedLegends.push(payload.legend);

            bus.$emit(EVENTS.RE_RENDER_CROP);
          }
          break;
        case false:
          bus.selectedLegends.splice(legendIndex, 1);

          bus.$emit(EVENTS.RE_RENDER_CROP);
          break;
        default:
          break;
      }
    },
    filterClasses(selectedClass) {
      this.filterByClass = selectedClass;
    },
    handleCategoryDropdownItemClick(category) {
      if (!this.selectedCategories.find(item => item === category)) {
        this.selectedCategories.push(category);
      } else {
        this.selectedCategories.splice(this.selectedCategories.indexOf(category), 1);
      }
    },
    handleBrandDropdownItemClick(brand) {
      if (!this.selectedBrands.find(item => item === brand)) {
        this.selectedBrands.push(brand);
      } else {
        this.selectedBrands.splice(this.selectedBrands.indexOf(brand), 1);
      }
    },
    // el-dropdown'nın clearable özelliği olmadığı için manuel olarak eklendi
    clearClassFilterSelect() {
      this.filterByClass = null;
    },
    clearBrandFilterSelect() {
      this.selectedBrands = [];
    },
    clearCategoryFilterSelect() {
      this.selectedCategories = [];
    },
    handleNextImage() {
      const total_images = this.reportDataImages.length;

      if (total_images !== this.selectedImageNumber) {
        this.selectedImageNumber += 1;
        bus.$emit(EVENTS.SELECTED_IMAGE_NUMBER_CHANGED, this.selectedImageNumber);
      }
    },
    handlePreviousImage() {
      if (this.selectedImageNumber !== 1) {
        this.selectedImageNumber -= 1;
        bus.$emit(EVENTS.SELECTED_IMAGE_NUMBER_CHANGED, this.selectedImageNumber);
      }
    },
    filterByLegend(legend, type) {
      if (type === 'Planogram') {
        return this.planogramItemList.filter(item => item.error === legend);
      } if (type === 'Realogram') {
        return this.realogramItemList.filter(item => item.error === legend);
      }

      return [];
    },
    getReducedSkuList(list, legendItems) {
      return list.reduce((acc, item) => {
        const foundItem = legendItems.find(f => f.sku.id.toString() === item.class.id);

        if (foundItem) {
          acc.push({
            ...item,
            ...foundItem
          });
        }

        return acc;
      }, []);
    },
    getLegendRealogramPlanogramLists(realogramList, planogramList, legendState) {
      return {
        realogram: realogramList.map((sku) => {
          const realogramSku = this.skus.find(item => item.id.toString() === sku.class.id);

          return {
            ...sku,
            ...realogramSku,
            legendState
          };
        }),
        planogram: planogramList.map((sku) => {
          const planogramSku = this.skus.find(item => item.id.toString() === sku.class.id);

          return {
            ...sku,
            ...planogramSku,
            legendState
          };
        })
      };
    },
    disableSelect(isDisable) {
      document.onselectstart = function disableselect() {
        return !isDisable;
      };
    }
  }
};
</script>

<style lang="scss">
.inspector-dialog {
  margin: 0 auto;
  border-radius: 20px;

  @include breakpoint(lg-max) {
    width: 100% !important;
    height: 100vh;
    margin: 0 !important;
    border-radius: 0;
  }

  .el-dialog__wrapper {
    top: 104px;
    left: 80px;

    @include breakpoint(lg-max) {
      top: 0;
      left: 0;
    }
  }

  .el-dialog__header {
    padding: 10px 20px;
    border-bottom: 1px solid #c7c7c7;

    @include breakpoint(lg-max) {
      padding: 8px 5px;
    }
  }

  .el-dialog__body {
    padding: 0 2px 15px 15px;

    @include breakpoint(lg-max) {
      height: auto;
      padding: 0;
    }
  }

  &-header {
    padding: 0 25px 0 0;
    margin-right: 38px;
    border-right: 1px solid #c7c7c7;

    @include breakpoint(lg-max) {
      display: none;
    }

    &-top {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding-bottom: 10px;
      border-bottom: 1px solid #e7e7e7;

      .filter-search-wrapper {
        display: flex;

        .inspector-filter-select {
          padding: 0;

          .el-input {
            width: 180px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            input {
              width: 180px;
              overflow: hidden;
              font-size: 14px;
              font-weight: 600;
              line-height: 19.07px;
              color: $cavernous;
              text-overflow: ellipsis;
              white-space: nowrap;
              border: none;

              &::placeholder {
                color: $cavernous;
              }
            }

            span {
              font-size: 14px;
              font-weight: 600;
              line-height: 19.07px;
              color: $cavernous;

              i {
                font-size: 18px;
                font-weight: bold;
                color: $darkSkyBlue;
              }
            }
          }
        }

        .inspector-filter-dropdown {
          margin-right: 15px;
          font-size: 14px;
          font-weight: 600;
          line-height: 19.07px;
          color: #535353;

          .filter-count {
            padding: 1px 6px;
            color: #fff;
            background-color: #5d9be5;
            border-radius: 10px;
          }

          .el-dropdown-link {
            i {
              font-size: 18px;
              font-weight: bold;
              color: $darkSkyBlue;
            }
          }
        }

        .search-and-buttons {
          display: flex;

          @media (max-width: 1190px) {
            flex-direction: column;
            row-gap: 5px;
          }

          .search-btn {
            float: right;
            max-width: 142px;
            height: 30px;

            input {
              border-radius: 15px;
            }

            .el-input__icon {
              font-size: 18px;
              font-weight: bold;
              color: #7c7c7c;
            }
          }

          .scene-action-buttons-wrapper {
            display: flex;

            @media (max-width: 1190px) {
              justify-content: flex-end;
            }
          }
        }
      }
    }

    &-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 10px;

      .bottom-left-side-wrapper {
        display: flex;
      }

      .bottom-right-side-wrapper {
        display: flex;
        align-items: center;
      }

      .el-switch {
        margin-right: 30px;

        &__core {
          height: 30px;
          border-radius: 15px;

          &::after {
            top: 2px;
            width: 24px;
            height: 24px;
          }
        }

        &__label {
          span {
            white-space: nowrap;
          }
        }

        &.is-checked {
          .el-switch__core {
            &::after {
              left: 83%;
            }
          }
        }
      }
    }
  }

  &-main {
    position: relative;

    .mobile-sku-list {
      display: none;

      @include breakpoint(lg-max) {
        position: fixed;
        bottom: 74px;
        display: flex;
        width: 100%;
        padding: 5px 0;
        overflow-x: scroll;
        background-color: $white;

        &.sku-drawer-removed {
          bottom: 37px;
        }

        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    .side-sku-list {
      @include breakpoint(lg-max) {
        display: none;
      }
    }

    .recognition-result-scene {
      position: relative;

      .left-button {
        position: absolute;
        top: 50%;
        left: 0;
        z-index: 1;
        filter:
          brightness(0) saturate(100%) invert(86%) sepia(0%)
          saturate(1022%) hue-rotate(156deg) brightness(87%) contrast(88%);
      }

      .right-button {
        position: absolute;
        top: 50%;
        right: 0;
        z-index: 1;
        margin-right: 15px;
        filter:
          brightness(0) saturate(100%) invert(86%) sepia(0%)
          saturate(1022%) hue-rotate(156deg) brightness(87%) contrast(88%);
      }

      .el-button.is-disabled {
        filter: none;
      }
    }

    .result-scene {
      position: relative;

      .left-button {
        position: absolute;
        top: 50%;
        left: 0;
        z-index: 1;
        filter:
          brightness(0) saturate(100%) invert(86%) sepia(0%)
          saturate(1022%) hue-rotate(156deg) brightness(87%) contrast(88%);
      }

      .right-button {
        position: absolute;
        top: 50%;
        right: 0;
        z-index: 1;
        margin-right: 15px;
        filter:
          brightness(0) saturate(100%) invert(86%) sepia(0%)
          saturate(1022%) hue-rotate(156deg) brightness(87%) contrast(88%);
      }

      .el-button.is-disabled {
        filter: none;
      }
    }
  }

  .el-dialog__headerbtn {
    top: 40px;
    right: 24px;

    i {
      font-size: 21px;
      color: #d1d1d1;
    }

    @include breakpoint(lg-max) {
      top: 12px;
      right: 12px;

      i {
        font-size: 18px;
      }
    }
  }

  &-header-mobile {
    display: none;

    @include breakpoint(lg-max) {
      display: flex;
      flex-direction: column;

      .inspector-dialog-header-top-mobile,
      .inspector-dialog-header-bottom-mobile {
        display: flex;
      }

      .inspector-dialog-header-top-mobile {
        align-items: center;
        justify-content: space-between;
        padding: 4px;
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);

        .btn-wrapper {
          display: flex;
        }
      }

      .switch-search-group {
        justify-content: space-between;
      }

      .inspector-dialog-header-bottom-mobile {
        align-items: center;
        justify-content: space-between;
        padding-top: 10px;
      }

      .el-switch {
        margin-right: 30px;

        &__core {
          height: 30px;
          border-radius: 15px;

          &::after {
            top: 2px;
            width: 24px;
            height: 24px;
          }
        }

        &__label {
          span {
            font-size: 10px;
            font-weight: 600;
            color: #535353;
          }

          &--right {
            margin-left: 7px;
          }
        }

        &.is-checked {
          .el-switch__core {
            &::after {
              left: 83%;
            }
          }
        }
      }

      .search-btn {
        float: none;
        max-width: 86px;

        input {
          border-radius: 15px;
        }

        .el-input__icon {
          font-size: 18px;
          font-weight: bold;
          color: #7c7c7c;
        }
      }

      .filter-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 28px;
        padding: 7px 10px;
        margin-left: 4px;
      }

      .mdi-filter-variant {
        font-size: 18px;
        color: #818181;
      }

      .mdi-import {
        font-size: 18px;
        color: #5d9be5;
        transform: rotate(-90deg);
      }

      .close-btn {
        padding-left: 11px;
        font-size: 24px;
        color: $greyShingle;
        cursor: pointer;
        border-left: 1px solid #dfdfdf;
      }
    }
  }

  .el-switch {
    align-self: center;
    margin-right: 30px;

    &__core {
      height: 30px;
      border-radius: 15px;

      &::after {
        top: 2px;
        width: 24px;
        height: 24px;
      }
    }

    &.is-checked {
      .el-switch__core {
        &::after {
          left: 83%;
        }
      }
    }
  }
}

// Mobile version inner filter dialog
.inspector-inner-dialog {
  @include breakpoint(lg-max) {
    .el-dialog {
      margin: 0 !important;

      .el-dialog__header {
        display: flex;
        justify-content: flex-end;
        padding: 10px 18px 0 0;

        i {
          font-size: 21px;
          color: #818181;
        }
      }
    }

    .inspector-filter-select-mobile {
      width: 100%;
      padding-bottom: 20px;
      border-bottom: 1px solid #ececec;

      .el-select__tags {
        top: 29px;
      }

      &:nth-child(2) {
        padding-top: 20px;
        border-top: 1px solid #ececec;
      }

      &:last-child {
        padding-top: 20px;
        border-bottom: none;
      }

      .el-input {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        input {
          width: 180px;
          overflow: hidden;
          font-size: 14px;
          font-weight: 600;
          line-height: 19.07px;
          color: $cavernous;
          text-overflow: ellipsis;
          white-space: nowrap;
          border: none;

          &::placeholder {
            color: $cavernous;
          }
        }

        span {
          font-size: 14px;
          font-weight: 600;
          line-height: 19.07px;
          color: $cavernous;

          i {
            font-size: 18px;
            font-weight: bold;
            color: $darkSkyBlue;
          }
        }
      }

      .select-popper {
        position: initial !important;
        border: none;
        box-shadow: 0 0 0 0;
      }
    }
  }
}

.v-modal {
  margin-top: 104px;
  margin-left: 80px;

  @include breakpoint(lg-max) {
    margin-top: 0;
    margin-left: 0;
  }
}

.el-dropdown-menu {
  &.report-inspector-dialog-dropdown {
    min-width: 128px;
    max-height: 300px;
    overflow-y: auto;

    .el-dropdown-menu__item {
      display: flex;
      justify-content: flex-start !important;
      padding: 0 20px;
      margin-bottom: 12px;
      font-weight: 400;
      color: #606266;

      .select-label {
        font-size: 14px;
        font-weight: 400;
        color: #606266;
      }

      i {
        position: absolute;
        left: 4px;
        margin-right: 6px;
        font-size: 14px;

        &.check {
          font-size: 14px;
        }
      }

      &.selected {
        color: #409eff;
        background-color: #fff;

        .select-label {
          font-size: 14px;
          font-weight: 600;
          color: #409eff;
        }
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &::-webkit-scrollbar {
      width: 4px;
      -webkit-appearance: none;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      min-height: 8px;
      max-height: 8px;
      background: #d6dee2;
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #bdc9d1;
    }

    li {
      font-size: 12px;
      font-weight: 600;
      line-height: 27px;
    }
  }
}

.el-drawer {
  .el-drawer__body {
    overflow: hidden;
  }

  &.custom-drawer {
    border-radius: 8px 8px 0 0;
    box-shadow: 0 -3px 0 rgba(0, 0, 0, 0.15);

    .el-drawer__header {
      margin-bottom: 0;
    }
  }

  &.btt {
    bottom: 39px;
  }
}

.scene-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .scene-titles {
    display: flex;

    @media (max-width: 800px) {
      flex-direction: column;
      align-items: center;
      width: 100%;
    }
  }

  .scene-title {
    display: block;
    padding: 3px 15px;
    font-size: 12px;
    font-weight: 600;
    color: #535353;
    border-left: 1px solid #ccc;

    &:first-child {
      padding-left: 0;
      border-left: none;
    }

    @media (max-width: 800px) {
      width: 100%;
      border-left: none;

      &.realogram-text {
        text-align: center;
      }

      &.planogram-text {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        justify-content: space-between;
        padding: 3px 15px;

        span:first-child {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .scene-actions-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 30px;

    @include breakpoint(lg-max) {
      display: none;
    }

    .background-slider {
      margin-right: 30px;
    }
  }

  .fullscreen-close-btn {
    position: absolute;
    right: -6px;
    display: inherit;
    padding: 2px 5px;
    font-size: 21px;
    border: 1.5px solid #e2e2e2;
    border-radius: 25px;
  }

  @include breakpoint(lg-max) {
    justify-content: center;
    margin-bottom: 3px;
  }
}

.inspector-cursor {
  cursor: pointer;
}

.hidden-sku-list {
  position: relative;
  width: 64px;
  height: calc(100vh - 329px);
  box-shadow: -4px 0 4px -4px rgba(0, 0, 0, 0.25);

  @include breakpoint(lg-max) {
    display: none;
  }

  .show-sku-list-btn {
    position: absolute;
    top: 12px;
    left: -22px;
    padding: 4px;
    font-size: 21px;
    color: #fff;
    cursor: pointer;
    background-color: #5d9be5;
    border-radius: 100%;
  }

  p {
    padding: 8px 15px;
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
    color: #4a90e2;
  }
}

.inspector-dialog-close-btn {
  position: absolute;
  top: 40px;
  right: 18px;
  font-size: 24px;
  color: $greyShingle;
  cursor: pointer;

  @include breakpoint(lg-max) {
    display: none;
  }
}

.report-inspector-mobile-bottom-section {
  .inspector-dialog-bottom-mobile {
    display: none;

    @include breakpoint(lg-max) {
      display: inherit;
      width: 100%;

      .drawer-button-wrapper {
        position: fixed;
        bottom: 40px;
        display: flex;
        width: 100%;
        background-color: #fff;

        .el-button {
          flex: 1;
          height: 35px;
          background-color: #5b5b5b;
          border-radius: 12px 12px 0 0;
        }
      }
    }
  }

  .mobile-scene-actions {
    display: none;

    @include breakpoint(lg-max) {
      position: fixed;
      bottom: 0;
      z-index: 4000;
      display: flex;
      align-items: center;
      width: 100%;
      background-color: #fff;
    }
  }
}

.scene-fullscreen {
  &.fullscreen-inspector {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 9 !important;
    width: 100% !important;
    height: 100% !important;
    min-height: -webkit-fill-available;
    overflow-y: auto !important;
    background-color: $white !important;

    .result-scene {
      width: 100% !important;
      padding: 10px;
    }

    .mobile-sku-list {
      bottom: 37px !important;
    }
  }
}
</style>
